import debounce from 'debounce-promise';
import {
  NestedSearch,
  LocationIQReverseLocation,
  LocationIQResult,
  TODO,
} from '@habity/types';
import { Stack, Text, useToast } from '@chakra-ui/react';
import { useUIStore } from '~/store';
import { useHomesStore } from '~/store/useHomesStore';
import { getOSMDataFromString, getOSMString } from '~/lib/places';
import { getOSMPlaces } from '~/lib/places';
import { getAPI, postAPI } from '~/lib/api';
import { useAgency } from './useAgency';

export const useLocation = () => {
  const loading = useUIStore((state) => state.loading);
  const setLoading = useUIStore((state) => state.setLoading);
  const setLocations = useHomesStore((state) => state.setLocations);
  const toast = useToast();
  const { agency } = useAgency();
  const { default_autocomplete_options } = agency || {};
  // const searchHistory = usePersistedStore(
  //   useSearchHistoryStore,
  //   (state) => state.searchHistory
  // );
  // const { agency } = useAgency();

  const mapResults = (results: Partial<LocationIQResult>[]) => {
    // const history =
    //   searchHistory?.map((item) => {
    //     return {
    //       value: '',
    //       display_place: getTitleFromFilters(agency, item),
    //       description: 'Sökhistorik',
    //       label: (
    //         <Stack spacing="0">
    //           <Text fontWeight="bold">{getTitleFromFilters(agency, item)}</Text>
    //           <Text fontWeight="normal" fontSize="xs">
    //             Sökhistorik
    //           </Text>
    //         </Stack>
    //       ),
    //     };
    //   }) || [];
    const allResults = results;
    return allResults.map((suggestion: TODO) => {
      return {
        ...suggestion,
        label: (
          <Stack spacing="0">
            <Text fontWeight="bold">{suggestion.display_place}</Text>
            <Text fontWeight="normal" fontSize="xs">
              {suggestion.description}
            </Text>
          </Stack>
        ),
      };
    });
  };

  const _suggestLocations = async (inputValue: string) => {
    if (
      !inputValue ||
      inputValue.length < 3
      // (locations && locations.length > 3)
    )
      return mapResults(
        default_autocomplete_options
          ? (default_autocomplete_options as LocationIQResult[])
          : []
      );

    // Encode the input value for URL path parameter
    // Browser will handle the encoding, but we need to ensure special chars are safe
    const results: LocationIQResult[] = await getAPI(
      `/providers/locationiq/autocomplete/${encodeURIComponent(inputValue)}`
    );

    setLoading({ ...loading, resultsLoading: false });
    return mapResults(results);
  };

  const _fetchLocations = async (
    filters?: Partial<NestedSearch> | null,
    locationsToAdd?: LocationIQReverseLocation[] | null,
    setAfterFetch = true
  ) => {
    // Fetch locations that might be missing
    const { places, streets, city, zips } = filters || {};

    const newLocations =
      locationsToAdd && locationsToAdd.length > 0
        ? locationsToAdd.filter(
            (l) => places && places.includes(getOSMString(l))
          )
        : [];

    for (const street of streets || []) {
      if (street && !newLocations.find((l) => l.label.includes(street)))
        newLocations.push({
          label: street,
          value: street.replaceAll(' ', '').trim(),
          class: 'highway', // Important to filter out when adding more places
          osm_type: 'way', // Important to filter out when adding more places
        } as TODO as LocationIQReverseLocation);
    }

    if (
      city &&
      (!streets || streets.length === 0) &&
      !newLocations.find((l) => l.label.includes(city))
    )
      newLocations.push({
        label: city,
        value: city.replaceAll(' ', '').trim(),
        class: 'highway', // Important to filter out when adding more places
        osm_type: 'way', // Important to filter out when adding more places
      } as TODO as LocationIQReverseLocation);

    for (const zip of zips || []) {
      if (zip && !newLocations.find((l) => l.label.includes(zip)))
        newLocations.push({
          label: zip,
          value: Number(zip.replaceAll(' ', '').trim()),
          class: 'highway', // Important to filter out when adding more places
          osm_type: 'way', // Important to filter out when adding more places
        } as TODO as LocationIQReverseLocation);
    }

    for (const place of getOSMPlaces(places)) {
      const { osm_id, osm_type } = getOSMDataFromString(place) || {};
      if (!osm_id || !osm_type) continue;

      const location: LocationIQReverseLocation = await postAPI(
        '/providers/locationiq/reverse',
        JSON.stringify({
          osm_id,
          osm_type,
          output_type: 'polygon_text',
          locationData: newLocations?.find(
            (l) => l.osm_id === osm_id.toString()
          ),
        })
      );

      if (!location) {
        // This will be triggered if an autocompleted location is present that has no data in the reverse geocoding API
        toast({
          title: 'Platsen hittades inte',
          description: `Inga koordinater kunde hämtas för O:${osm_type}:${osm_id}.`,
          status: 'error',
        });
        return;
      }
      // Add/update location data with new polygon
      const existingLocation = newLocations.findIndex(
        (l) => getOSMString(l) === place
      );
      if (existingLocation > -1) {
        newLocations[existingLocation] = location;
      } else {
        newLocations.push(location);
      }
    }
    if (setAfterFetch) setLocations([]); // TODO: Implement the new AutocompleteItem here
    return newLocations;
  };

  const suggestLocations = debounce(_suggestLocations, 100);

  const _geocodeAddress = async (
    street: string,
    city: string,
    streetWithoutNumber?: string
  ): Promise<{ lat: string | null; lon: string | null; found: boolean }> => {
    try {
      const result = await postAPI(
        '/providers/locationiq/geocode',
        JSON.stringify({ street, city, streetWithoutNumber })
      );

      return result || { lat: null, lon: null, found: false };
    } catch (error) {
      console.error('Geocoding error:', error);
      return { lat: null, lon: null, found: false };
    }
  };

  const fetchLocations = debounce(_fetchLocations, 100);

  const geocodeAddress = debounce(_geocodeAddress, 100);

  return {
    suggestLocations,
    fetchLocations,
    geocodeAddress,
  };
};
